import { useParams } from 'react-router-dom';

import { useAuth } from '../../../../../../core/auth';
import { useAppDispatch, useAppSelector } from '../../../../../../lib';
import { selectBatchId } from '../../../../../supervisor-queue/state/selector';
import type {
  Annotation,
  Attribute,
  Span,
} from '../../../../../workspace-common/types/responseTypes';
import type { FormAttributeValue } from '../../../../../workspace-common/types/types';
import {
  buildAttributeValues,
  isSaveable,
} from '../../../../../workspace-common/utils/annotationCore';
import { useAnnotationFormCore } from '../../../../../workspace-common/utils/useAnnotationFormCore';
import {
  useAddResolvedAnnotationSpansMutation,
  useGetSupervisorWorkspaceQuery,
  useSupervisorAddFinalizedAnnotationMutation,
  useSupervisorRejectAndCreateAnnotationMutation,
  useUpdateSuperviseAttributesMutation,
} from '../../../../api/supervisorApiSlice';
import { SUPERVISOR_TAG } from '../../../../constants';
import {
  selectAddEvidenceModeForConceptId,
  selectAnnotationIdsToRejectByConflictId,
  selectCreateAnnotationError,
  selectEditAnnotationError,
  selectEvidenceIdsToDelete,
  selectPendingSpansForAnnotation,
} from '../../../../state/selector';
import {
  clearAddEvidenceModeForConceptId,
  clearCreateAnnotationError,
  clearEditAnnotationError,
  clearEvidenceIdsToDelete,
  clearHighlightedTextSpans,
  clearPendingSpansForAnnotation,
  clearNewAnnotationFormDirty,
  removeFromAnnotationIdsInEditMode,
  removeFromConflictIdsInReplaceMode,
  removeFromFinalizedConceptIdsInCreateMode,
  removeFromResolvedAnnotationIdsInReplaceMode,
  setAddEvidenceModeForConceptId,
  setCreateAnnotationError,
  setEditAnnotationError,
  setHighlightedTextSpans,
  setNewAnnotationFormDirty,
} from '../../../../state/supervisorSlice';

interface UseInProgressAnnotationCardResults {
  formAttributes: FormAttributeValue[];
  isAttributeVisible: (name: string) => boolean;
  getAttributeRule: (name: string) => Attribute | undefined;
  isSaveAttributesLoading: boolean;
  handleSaveAnnotation: () => Promise<void>;
  handleChangeAttribute: (name: string, updates: Partial<FormAttributeValue>) => void;
  isPresent: boolean;
  isPresentNull: boolean;
  isSaveSpansLoading: boolean;
  evidenceRequired: boolean;
  addEvidenceModeForConceptId: string | null;
  currentAnnotationId: string;
  startAnnotationMode: (annotation: Annotation | null) => void;
  enableSaveButton: boolean;
  handleCancelEdit: () => void;
  handleCancelReplace: (conflictId: string | undefined) => void;
  handleCancelFinalizedCreate: () => void;
  handleCancelResolvedReplace: () => void;
  editAnnotationError: string | null;
  closeEditAnnotationError: () => void;
  createAnnotationError: string | null;
  closeCreateAnnotationError: () => void;
}

interface UseInProgressAnnotationCardProps {
  annotation: Annotation | null;
  conceptId: string;
  conflictId?: string;
  isFinalizedCreate?: boolean;
  resolvedAnnotationIdToDelete?: string;
}

export const useInProgressAnnotationCard = ({
  annotation,
  conceptId,
  conflictId,
  isFinalizedCreate = false,
  resolvedAnnotationIdToDelete,
}: UseInProgressAnnotationCardProps): UseInProgressAnnotationCardResults => {
  const { encounterId } = useParams();
  const { user } = useAuth();
  const batchId = useAppSelector(selectBatchId);
  const { data: supervisorWorkspaceData } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser }
  );
  const dispatch = useAppDispatch();

  const concept = supervisorWorkspaceData?.data?.concepts.find((c) => c.conceptId === conceptId);
  const attributes = concept?.attributes;

  const annotationIdsToReject = useAppSelector(
    selectAnnotationIdsToRejectByConflictId(conflictId ?? '')
  );
  const evidenceIdsToDeleteMap = useAppSelector(selectEvidenceIdsToDelete);
  const evidenceIdsToDelete = evidenceIdsToDeleteMap[annotation?.annotationId || ''] || [];

  const {
    formAttributes,
    getAttributeRule,
    isAttributeVisible,
    handleChangeAttribute,
    isPresent,
    isPresentNull,
    getTemporaryAnnotationId,
    currentAnnotationId,
    pendingSpans,
    evidenceRequired,
    addEvidenceModeForConceptId,
    startAnnotationMode,
    enableSaveButton,
  } = useAnnotationFormCore({
    annotation,
    conceptId,
    editingAnnotationId: conflictId,
    attributes,
    selectors: { selectAddEvidenceModeForConceptId, selectPendingSpansForAnnotation },
    actions: {
      clearPendingSpansForAnnotation,
      clearHighlightedTextSpans,
      clearAddEvidenceModeForConceptId,
      setAddEvidenceModeForConceptId,
      setHighlightedTextSpans,
    },
    evidenceIdsToDelete,
  });

  const [updateSuperviseAttributes, { isLoading: isUpdateSuperviseAttributesLoading }] =
    useUpdateSuperviseAttributesMutation();
  const [
    supervisorRejectAndCreateAnnotation,
    { isLoading: isSupervisorRejectAndCreateAnnotationLoading },
  ] = useSupervisorRejectAndCreateAnnotationMutation();
  const [
    supervisorAddFinalizedAnnotation,
    { isLoading: isSupervisorAddFinalizedAnnotationLoading },
  ] = useSupervisorAddFinalizedAnnotationMutation();
  const [addResolvedAnnotationSpans, { isLoading: isAddResolvedAnnotationSpansLoading }] =
    useAddResolvedAnnotationSpansMutation();

  const editAnnotationErrorForId = useAppSelector(
    selectEditAnnotationError(annotation?.annotationId ?? '')
  );
  const editAnnotationError = annotation?.annotationId ? editAnnotationErrorForId : null;

  const closeEditAnnotationError = () => dispatch(clearEditAnnotationError());

  const createAnnotationError = useAppSelector(selectCreateAnnotationError(conceptId));
  const closeCreateAnnotationError = () => dispatch(clearCreateAnnotationError());

  const handleChangeAttributeTracked = (name: string, updates: Partial<FormAttributeValue>) => {
    if (!annotation) {
      dispatch(setNewAnnotationFormDirty(conceptId));
    }
    handleChangeAttribute(name, updates);
  };

  const handleSaveAnnotation = async () => {
    if (!encounterId || !batchId) return;

    const attributeValues = buildAttributeValues(
      attributes || [],
      formAttributes,
      isAttributeVisible
    );

    if (!annotation?.annotationId) {
      try {
        const spansToSave = pendingSpans.map((span: Span) => ({
          noteId: span.noteId,
          offsetStart: span.offsetStart,
          offsetEnd: span.offsetEnd,
          text: span.text,
        }));
        if (resolvedAnnotationIdToDelete) {
          await supervisorAddFinalizedAnnotation({
            encounterId,
            batchId,
            conceptId,
            attributes: attributeValues,
            spans: spansToSave,
            annotationIdsToDelete: [resolvedAnnotationIdToDelete],
          }).unwrap();
          dispatch(clearPendingSpansForAnnotation(getTemporaryAnnotationId()));
          dispatch(clearHighlightedTextSpans());
          dispatch(clearAddEvidenceModeForConceptId());
          dispatch(removeFromResolvedAnnotationIdsInReplaceMode(conceptId));
          dispatch(clearNewAnnotationFormDirty(conceptId));
          dispatch(clearEditAnnotationError());
          dispatch(clearCreateAnnotationError());
        } else if (isFinalizedCreate) {
          await supervisorAddFinalizedAnnotation({
            encounterId,
            batchId,
            conceptId,
            attributes: attributeValues,
            spans: spansToSave,
          }).unwrap();
          dispatch(clearPendingSpansForAnnotation(getTemporaryAnnotationId()));
          dispatch(clearHighlightedTextSpans());
          dispatch(clearAddEvidenceModeForConceptId());
          dispatch(removeFromFinalizedConceptIdsInCreateMode(conceptId));
          dispatch(clearNewAnnotationFormDirty(conceptId));
          dispatch(clearEditAnnotationError());
          dispatch(clearCreateAnnotationError());
        } else {
          await supervisorRejectAndCreateAnnotation({
            encounterId,
            conflictId: conflictId ?? '',
            annotationIdsToReject,
            conceptId,
            attributes: attributeValues,
            spans: spansToSave,
          }).unwrap();
          dispatch(clearPendingSpansForAnnotation(getTemporaryAnnotationId()));
          dispatch(clearHighlightedTextSpans());
          dispatch(clearAddEvidenceModeForConceptId());
          dispatch(removeFromConflictIdsInReplaceMode(getTemporaryAnnotationId()));
          dispatch(clearNewAnnotationFormDirty(conceptId));
          dispatch(clearEditAnnotationError());
          dispatch(clearCreateAnnotationError());
        }
      } catch (err: any) {
        const message =
          err?.data?.detail ||
          err?.data?.message ||
          err?.data?.errorMessage ||
          err?.message ||
          'Failed to create annotation';
        dispatch(setCreateAnnotationError({ conceptId: conceptId, error: message }));
      }
      return;
    }

    try {
      const attributesChanged = isSaveable(
        annotation.attributes || [],
        formAttributes,
        attributes || []
      );

      const spansToSave =
        pendingSpans.length > 0
          ? pendingSpans.map((span) => ({
              noteId: span.noteId,
              offsetStart: span.offsetStart,
              offsetEnd: span.offsetEnd,
              text: span.text,
            }))
          : undefined;

      const savedSpanEvidenceIdsToDelete =
        !isPresent && annotation.spans
          ? annotation.spans
              .map((s) => s.evidenceId)
              .filter((id): id is string => !!id && !evidenceIdsToDelete.includes(id))
          : [];

      const allEvidenceIdsToDelete = [...evidenceIdsToDelete, ...savedSpanEvidenceIdsToDelete];
      const evidenceIdsToDeletePayload =
        allEvidenceIdsToDelete.length > 0 ? allEvidenceIdsToDelete : undefined;

      if (attributesChanged || evidenceIdsToDeletePayload) {
        try {
          await updateSuperviseAttributes({
            annotationId: annotation.annotationId,
            attributes: attributeValues,
            spans: spansToSave,
            evidenceIdsToDelete: evidenceIdsToDeletePayload,
          }).unwrap();
          if (spansToSave) dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
          dispatch(clearEvidenceIdsToDelete({ annotationId: annotation.annotationId }));
          dispatch(clearHighlightedTextSpans());
          dispatch(clearAddEvidenceModeForConceptId());
          dispatch(removeFromAnnotationIdsInEditMode(annotation.annotationId));
        } catch (err: any) {
          const message =
            err?.data?.detail ||
            err?.data?.message ||
            err?.data?.errorMessage ||
            err?.message ||
            'Failed to edit annotation';
          dispatch(
            setEditAnnotationError({ annotationId: annotation.annotationId, error: message })
          );
        }
      } else if (spansToSave) {
        await addResolvedAnnotationSpans({
          annotationId: annotation.annotationId,
          spans: spansToSave,
        }).unwrap();
        dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
        dispatch(clearHighlightedTextSpans());
        dispatch(clearAddEvidenceModeForConceptId());
        dispatch(clearEditAnnotationError());
        dispatch(clearCreateAnnotationError());
      }
    } catch (err: any) {
      const message =
        (err as { data: { detail?: string; message?: string } }).data.detail ||
        (err as { data: { detail?: string; message?: string } }).data.message ||
        'Failed to edit annotation';
      dispatch(setEditAnnotationError({ annotationId: annotation.annotationId, error: message }));
    }
  };

  const handleCancelEdit = () => {
    if (annotation) {
      dispatch(clearPendingSpansForAnnotation(annotation.annotationId));
      dispatch(clearEvidenceIdsToDelete({ annotationId: annotation.annotationId }));
      dispatch(clearAddEvidenceModeForConceptId());
      dispatch(clearHighlightedTextSpans());
      dispatch(removeFromAnnotationIdsInEditMode(annotation.annotationId));
      dispatch(clearEditAnnotationError());
      dispatch(clearCreateAnnotationError());
    }
  };
  const handleCancelReplace = (conflictId: string | undefined) => {
    if (conflictId) {
      dispatch(removeFromConflictIdsInReplaceMode(conflictId));
      dispatch(clearNewAnnotationFormDirty(conceptId));
      dispatch(clearEditAnnotationError());
      dispatch(clearCreateAnnotationError());
    }
  };

  const handleCancelFinalizedCreate = () => {
    dispatch(clearPendingSpansForAnnotation(getTemporaryAnnotationId()));
    dispatch(clearHighlightedTextSpans());
    dispatch(clearAddEvidenceModeForConceptId());
    dispatch(clearCreateAnnotationError());
    dispatch(clearNewAnnotationFormDirty(conceptId));
    dispatch(removeFromFinalizedConceptIdsInCreateMode(conceptId));
  };

  const handleCancelResolvedReplace = () => {
    dispatch(clearPendingSpansForAnnotation(getTemporaryAnnotationId()));
    dispatch(clearHighlightedTextSpans());
    dispatch(clearAddEvidenceModeForConceptId());
    dispatch(clearCreateAnnotationError());
    dispatch(clearNewAnnotationFormDirty(conceptId));
    dispatch(removeFromResolvedAnnotationIdsInReplaceMode(conceptId));
  };

  const enableSaveButtonFinal =
    enableSaveButton || (!!annotation && evidenceIdsToDelete.length > 0 && !evidenceRequired);

  return {
    formAttributes,
    isAttributeVisible,
    getAttributeRule,
    isSaveAttributesLoading:
      isUpdateSuperviseAttributesLoading ||
      isSupervisorRejectAndCreateAnnotationLoading ||
      isSupervisorAddFinalizedAnnotationLoading,
    handleSaveAnnotation,
    handleChangeAttribute: handleChangeAttributeTracked,
    isPresent,
    isPresentNull,
    evidenceRequired,
    addEvidenceModeForConceptId,
    currentAnnotationId,
    startAnnotationMode,
    enableSaveButton: enableSaveButtonFinal,
    isSaveSpansLoading: isAddResolvedAnnotationSpansLoading,
    handleCancelEdit,
    handleCancelReplace,
    handleCancelFinalizedCreate,
    handleCancelResolvedReplace,
    editAnnotationError,
    closeEditAnnotationError,
    createAnnotationError,
    closeCreateAnnotationError,
  };
};
